const identitySrcResolver = (src) => src;

/**
 * Decoded-image cache keyed by element `src`. One HTMLImageElement per unique
 * source; `onImageLoad` lets the renderer repaint once a bitmap arrives.
 * `resolveSrc` maps persisted srcs (e.g. `netpad-blob:` asset refs) to
 * loadable URLs while cache keys stay the persisted form.
 * @param {{ onImageLoad: Function, resolveSrc?: (src: string) => string }} params
 * @returns {{ dispose: Function, getImage: (src: string) => HTMLImageElement | null }}
 */
export const createImageCache = ({
  onImageLoad,
  resolveSrc = identitySrcResolver,
}) => {
  const imagesBySrc = new Map();

  const getImage = (src) => {
    if (!src) return null;
    const cachedImage = imagesBySrc.get(src);
    if (cachedImage) return cachedImage.complete ? cachedImage : null;

    const image = new Image();
    image.onload = () => onImageLoad();
    image.src = resolveSrc(src);
    imagesBySrc.set(src, image);

    return image.complete ? image : null;
  };

  const dispose = () => {
    imagesBySrc.forEach((image) => {
      image.onload = null;
      image.src = "";
    });
    imagesBySrc.clear();
  };

  return { dispose, getImage };
};
